import type { CampaignGroupTotal, CampaignListItem } from "@gen/tracker/v1/campaign_pb";
import { MetricFormat } from "@gen/tracker/v1/reporting_pb";
import type { StatsRow } from "@gen/tracker/v1/reporting_pb";

import { csvEscape } from "@/lib/csv";

import { OUTCOME, OUTCOME_PARTS, type ListColumn } from "./list-columns";
import type { Fold } from "./list-url";

/**
 * The table's rows (boards CampList, CampGroups): a campaign is a line of
 * the server's numbers; in a fold, a group's header line is the server's
 * `group_totals` entry over its campaigns. Pure — nothing is summed, a
 * header never adds up the rows under it.
 */

/** A metric bag: the server's decimal strings as numbers; a blank stays absent. */
export type Bag = Record<string, number>;

export function bagOf(row: StatsRow | undefined): Bag {
  const out: Bag = {};
  if (!row) return out;
  for (const [k, v] of Object.entries(row.values)) {
    const n = Number.parseFloat(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

export interface CampaignLine {
  kind: "campaign";
  /** Unique across the table: a campaign under two offers is two lines. */
  id: string;
  campaignId: string;
  name: string;
  item: CampaignListItem;
  v: Bag;
  prev?: Bag;
  /** Indented under a fold's header. */
  nested: boolean;
}

export interface GroupLine {
  kind: "group";
  id: string;
  key: string;
  name: string;
  /** How many of the listed campaigns fall in it — the server's. */
  campaigns: number;
  v: Bag;
  prev?: Bag;
}

export type Line = CampaignLine | GroupLine;

/** The fold's value of a campaign: the keys it falls under (by offer, every offer it routes to). */
export function foldKeys(item: CampaignListItem, fold: Fold): string[] {
  const c = item.campaign;
  switch (fold) {
    case "groups":
      return [c?.groupId ?? ""];
    case "buyers":
      return [c?.ownership?.ownerId ?? ""];
    case "offers":
      return item.offers.length > 0 ? item.offers.map((o) => o.id) : [""];
    default:
      return [];
  }
}

/** What a fold's header is called, read off its campaigns when one names it. */
export function foldLabelFromItems(items: readonly CampaignListItem[], fold: Fold): Map<string, string> {
  const out = new Map<string, string>();
  for (const item of items) {
    if (fold === "groups" && item.campaign?.groupId && item.groupName) out.set(item.campaign.groupId, item.groupName);
    if (fold === "buyers" && item.campaign?.ownership?.ownerId && item.ownerName) out.set(item.campaign.ownership.ownerId, item.ownerName);
    if (fold === "offers") for (const o of item.offers) if (o.name) out.set(o.id, o.name);
  }
  return out;
}

const valueOf = (v: Bag, key: string) => (key === "name" ? undefined : v[key]);

/**
 * The lines on screen. Flat: the campaigns in the server's order. Folded:
 * each header (in the order of the sort key over the headers' own numbers —
 * the server's — blanks last) followed by its campaigns in the server's
 * order. A page of the list shows the headers of its own campaigns only; a
 * header no campaign falls under (spend without clicks) stands on the first
 * page. Pure.
 */
export function buildLines(input: {
  items: readonly CampaignListItem[];
  fold: Fold;
  groupTotals: readonly CampaignGroupTotal[];
  label: (key: string) => string;
  sort: { key: string; desc: boolean };
  withCompare: boolean;
  /** Not the list's first page: campaign-less headers are the first page's. Default false. */
  laterPage?: boolean;
}): Line[] {
  const campaign = (item: CampaignListItem, nested: boolean, under = ""): CampaignLine => ({
    kind: "campaign",
    id: `c:${under}:${item.campaign?.id ?? ""}`,
    campaignId: item.campaign?.id ?? "",
    name: item.campaign?.name ?? "",
    item,
    v: bagOf(item.stats),
    prev: input.withCompare ? bagOf(item.compareStats) : undefined,
    nested,
  });
  if (input.fold === "list") return input.items.map((i) => campaign(i, false));

  const under = new Map<string, CampaignListItem[]>();
  for (const item of input.items) {
    for (const key of foldKeys(item, input.fold)) {
      const list = under.get(key);
      if (list) list.push(item);
      else under.set(key, [item]);
    }
  }
  const listed = input.groupTotals.filter((g) => under.has(g.key) || (g.campaigns === 0 && !input.laterPage));
  const headers: GroupLine[] = listed.map((g) => ({
    kind: "group",
    id: `g:${g.key}`,
    key: g.key,
    name: input.label(g.key),
    campaigns: g.campaigns,
    v: bagOf(g.stats),
    prev: input.withCompare ? bagOf(g.compareStats) : undefined,
  }));
  const { key, desc } = input.sort;
  headers.sort((a, b) => {
    if (key === "name") return desc ? b.name.localeCompare(a.name, "ru") : a.name.localeCompare(b.name, "ru");
    const x = valueOf(a.v, key);
    const y = valueOf(b.v, key);
    if (x === undefined && y === undefined) return 0;
    if (x === undefined) return 1;
    if (y === undefined) return -1;
    return desc ? y - x : x - y;
  });
  return headers.flatMap((h) => [h, ...(under.get(h.key) ?? []).map((i) => campaign(i, true, h.key))]);
}

/** The value a CSV cell holds: the server's number, a percent as a percent (5.1, not 0.051). Pure. */
export function csvValue(v: Bag, c: ListColumn): string {
  if (c.key === OUTCOME) return OUTCOME_PARTS.map((k) => String(v[k] ?? 0)).join("/");
  const x = v[c.key];
  if (x === undefined) return "";
  if (c.def?.format === MetricFormat.PERCENT) return String(Math.round(x * 1e6) / 1e4);
  return String(x);
}

/**
 * CSV = the screen: the same lines (a fold's header, then its campaigns), the
 * same columns in the same order under the same headers, then «Итого». Pure.
 */
export function linesToCsv(
  lines: readonly Line[],
  columns: readonly ListColumn[],
  words: { level: string; name: string; status: string; group: string; campaign: string; total: string },
  status: (line: CampaignLine) => string,
  totals: Bag | undefined,
): string {
  const out = [[words.level, words.name, words.status, ...columns.map((c) => c.label)].map(csvEscape).join(",")];
  for (const l of lines) {
    const cells = [l.kind === "group" ? words.group : words.campaign, l.name, l.kind === "campaign" ? status(l) : ""];
    for (const c of columns) cells.push(csvValue(l.v, c));
    out.push(cells.map(csvEscape).join(","));
  }
  if (totals) out.push([words.total, "", "", ...columns.map((c) => csvValue(totals, c))].map(csvEscape).join(","));
  return out.join("\r\n");
}
